import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

export type FeedbackCategory = "confusion" | "suggestion";

interface Props {
  docId: string;
  collection: "faq" | "sop";
  docVersion?: string;
  variant?: "standard" | "dictionary";
  inline?: boolean;
  /** When provided, the widget is "headless": the caller controls open state
   *  and picks the category. The internal "was any of this confusing?" prompt
   *  and the kind chip picker are hidden. */
  category?: FeedbackCategory;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, the note is required before Send enables. */
  requireNote?: boolean;
  /** Optional prompt shown at the top of the open panel. */
  promptLabel?: string;
  /** Called after a successful submission. */
  onFiled?: () => void;
  /** If set, the widget shows the thank-you briefly then resets so the
   *  reviewer can immediately file another note without remounting. When
   *  set, the sessionStorage "already sent" guard is skipped. */
  resetAfterFileMs?: number;
}


type Kind = "confusing" | "wrong_or_outdated" | "page_broken" | "missing_term";

const CHIPS: { value: Kind; label: string }[] = [
  { value: "confusing", label: "Something confused me" },
  { value: "wrong_or_outdated", label: "Something looks wrong or outdated" },
  { value: "page_broken", label: "Something on this page is broken" },
  { value: "missing_term", label: "A word here isn't in the dictionary" },
];

const sessionKey = (docId: string, category: FeedbackCategory) =>
  `doc_feedback_sent:${category}:${docId}`;

export function DocFeedbackWidget({
  docId,
  collection,
  docVersion,
  variant = "standard",
  inline = false,
  category,
  open,
  onOpenChange,
  requireNote = false,
  promptLabel,
  onFiled,
  resetAfterFileMs,
}: Props) {
  const isDict = variant === "dictionary";
  const controlled = typeof open === "boolean";
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const expanded = controlled ? (open as boolean) : uncontrolledOpen;
  const setExpanded = (v: boolean) => {
    if (!controlled) setUncontrolledOpen(v);
    onOpenChange?.(v);
  };

  const [kind, setKind] = useState<Kind | null>(
    isDict ? "missing_term" : category === "suggestion" ? "confusing" : null,
  );
  const [note, setNote] = useState("");
  const [term, setTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [alreadySent, setAlreadySent] = useState(false);

  const effectiveCategory: FeedbackCategory = category ?? "confusion";

  useEffect(() => {
    try {
      if (!resetAfterFileMs && sessionStorage.getItem(sessionKey(docId, effectiveCategory)))
        setAlreadySent(true);
      else setAlreadySent(false);
    } catch {}
    // Reset local UI when doc or category changes.
    setJustSubmitted(false);
    setNote("");
    setTerm("");
  }, [docId, effectiveCategory, resetAfterFileMs]);

  const needsTerm = kind === "missing_term";
  const noteFilled = note.trim().length > 0;
  const baseValid = !!kind && (!needsTerm || term.trim().length > 0);
  const canSubmit = baseValid && (!requireNote || noteFilled);

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id ?? null;
      const context =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.hash}`
          : null;
      await supabase.from("doc_feedback" as any).insert({
        doc_id: docId,
        collection,
        doc_version: docVersion ?? null,
        user_id: uid,
        kind,
        category: effectiveCategory,
        note: note.trim() || null,
        tag: null,
        suggested_term: needsTerm ? term.trim() : null,
        context,
      } as any);
      try {
        if (!resetAfterFileMs)
          sessionStorage.setItem(sessionKey(docId, effectiveCategory), "1");
      } catch {}
      setJustSubmitted(true);
      onFiled?.();
      if (resetAfterFileMs) {
        window.setTimeout(() => {
          setJustSubmitted(false);
          setNote("");
          setTerm("");
          setKind(isDict ? "missing_term" : effectiveCategory === "suggestion" ? "confusing" : null);
        }, resetAfterFileMs);
      }
    } catch (e) {
      console.error("doc_feedback insert failed", e);
      setJustSubmitted(true);
      onFiled?.();
    } finally {
      setSubmitting(false);
    }
  }

  const outerMargin = inline ? "" : "mt-8";
  const align = inline ? "text-left" : "text-center";
  const panelMargin = inline ? "mt-3" : "mt-8";
  const panelMx = inline ? "" : "mx-auto";

  if (justSubmitted) {
    return (
      <div className={`${outerMargin} ${panelMx} max-w-2xl rounded-md border bg-muted/20 p-4 md:p-5 text-sm text-muted-foreground`}>
        Thank you. We read every note and use them to make these pages better.
        We don't reply to these notes, so if you need help with your own
        situation,{" "}
        <Link to="/support" className="underline">
          open a support ticket
        </Link>{" "}
        and a person will answer.
      </div>
    );
  }

  if (alreadySent && !expanded) {
    if (controlled) return null;
    return (
      <div className={`${outerMargin} ${align} text-xs text-muted-foreground`}>
        Thanks for your note on this page.
      </div>
    );
  }

  if (!expanded) {
    // In controlled/headless mode, do not render our own entry button.
    if (controlled) return null;
    const label = isDict
      ? "Is there a word that should be defined here?"
      : "Was any of this confusing?";
    if (inline) {
      return (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted whitespace-nowrap bg-transparent p-0 m-0 border-0 font-normal leading-none"
        >
          {label}
        </button>
      );
    }
    return (
      <div className={`${outerMargin} ${align}`}>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 decoration-dotted"
        >
          {label}
        </button>
      </div>
    );
  }

  // Hide the kind chip picker when headless (caller has already picked
  // confusion vs suggestion via their own buttons).
  const showKindPicker = !isDict && !controlled;

  return (
    <div className={`${panelMargin} ${panelMx} max-w-2xl rounded-md border bg-muted/20 p-4 md:p-5 text-sm`}>
      {promptLabel && (
        <p className="text-xs font-medium text-muted-foreground mb-2">{promptLabel}</p>
      )}

      {isDict ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">The word or phrase</label>
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="e.g., ecclesiastical"
              className="text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">
              Where did you run into it? (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A page, a form, an email, a conversation..."
              className="text-sm min-h-[70px]"
            />
          </div>
        </div>
      ) : (
        <>
          {showKindPicker && (
            <div className="flex flex-wrap gap-2 mb-3">
              {CHIPS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setKind(c.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    kind === c.value
                      ? "bg-foreground text-background border-foreground"
                      : "bg-background hover:bg-muted"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          {needsTerm && (
            <div className="mb-3">
              <label className="block text-xs font-medium mb-1">The word or phrase</label>
              <Input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Type the word you didn't find"
                className="text-sm"
              />
            </div>
          )}
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              needsTerm ? "Any context? (optional)" : "A word, a sentence, anything."
            }
            className="text-sm min-h-[80px]"
          />
          {requireNote && !noteFilled && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              A suggestion needs words. Tell us the idea before sending.
            </p>
          )}
        </>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={!canSubmit || submitting}>
          {submitting && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
          Send
        </Button>
      </div>
    </div>
  );
}
